import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Input from '../components/Input';
import Textarea from '../components/Textarea';
import Select from '../components/Select';
import Button from '../components/Button';
import PhotoUploader, { Photo } from '../components/PhotoUploader';
import ListingNameInput from '../components/ListingNameInput';
import { Disclosure } from '@headlessui/react';
import { COMMON_TIMEZONES } from '../utils/timezone';

interface RenterDetailRequirement {
  fieldName: string;
  description: string;
  required: boolean;
}

interface ListingData {
  name: string;
  title: string;
  description: string;
  address: string;
  timezone: string;
  showOnMap: boolean;
  showAddress: string;
  showSupportPhone: string;
  supportPhoneNumber: string;
  advanceBookingLimit: number;
  cancellationWindow: { units: string; quantity: number };
  transitionTime: { units: string; quantity: number };
  renterDetailRequirements: RenterDetailRequirement[];
  photos: Photo[];
  youtubeVideos: string[];
  preferredSlotView: string;
  showCalendar: boolean;
  showAvailabilityAboveDescription: boolean;
  status: string;
  adminDisabled: boolean;
}

const EditListingPage: React.FC = () => {
  const { id: listingId } = useParams();
  const [formData, setFormData] = useState<ListingData>({
    name: '',
    title: '',
    description: '',
    address: '',
    timezone: 'America/New_York',
    showOnMap: false,
    showAddress: 'public',
    showSupportPhone: 'hide',
    supportPhoneNumber: '',
    advanceBookingLimit: 365,
    cancellationWindow: { units: 'hours', quantity: 24 },
    transitionTime: { units: 'minutes', quantity: 0 },
    renterDetailRequirements: [] as RenterDetailRequirement[],
    photos: [] as Photo[],
    youtubeVideos: [] as string[],
    preferredSlotView: 'list',
    showCalendar: true,
    showAvailabilityAboveDescription: false,
    status: 'active',
    adminDisabled: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [youtubeInput, setYoutubeInput] = useState('');
  const navigate = useNavigate();

  const fetchListing = useCallback(async () => {
    try {
      const { listing } = await api.get<{ listing: ListingData }>(`/listings/${listingId}`);
      setFormData({
        name: listing.name || '',
        title: listing.title || '',
        description: listing.description || '',
        address: listing.address || '',
        timezone: listing.timezone || 'America/New_York',
        showOnMap: listing.showOnMap || false,
        showAddress: listing.showAddress || 'public',
        showSupportPhone: listing.showSupportPhone || 'hide',
        supportPhoneNumber: listing.supportPhoneNumber || '',
        advanceBookingLimit: listing.advanceBookingLimit || 365,
        cancellationWindow: listing.cancellationWindow || { units: 'hours', quantity: 24 },
        transitionTime: listing.transitionTime || { units: 'minutes', quantity: 0 },
        renterDetailRequirements: listing.renterDetailRequirements || [],
        photos: listing.photos || [],
        youtubeVideos: listing.youtubeVideos || [],
        preferredSlotView: listing.preferredSlotView || 'list',
        showCalendar: listing.showCalendar !== undefined ? listing.showCalendar : true,
        showAvailabilityAboveDescription: listing.showAvailabilityAboveDescription || false,
        status: listing.status || 'active',
        adminDisabled: listing.adminDisabled || false,
      });
    } catch {
      toast.error('Failed to load listing');
      navigate('/my-listings');
    } finally {
      setIsLoading(false);
    }
  }, [listingId, navigate]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const addRenterDetailField = () => {
    setFormData({
      ...formData,
      renterDetailRequirements: [
        ...formData.renterDetailRequirements,
        { fieldName: '', description: '', required: false },
      ],
    });
  };

  const updateRenterDetailField = (
    index: number,
    field: keyof RenterDetailRequirement,
    value: string | boolean
  ) => {
    const updated = [...formData.renterDetailRequirements];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, renterDetailRequirements: updated });
  };

  const removeRenterDetailField = (index: number) => {
    const updated = formData.renterDetailRequirements.filter((_, i) => i !== index);
    setFormData({ ...formData, renterDetailRequirements: updated });
  };

  const addYoutubeVideo = () => {
    if (!youtubeInput.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    // Basic YouTube URL validation
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    if (!youtubeRegex.test(youtubeInput)) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    if (formData.youtubeVideos.includes(youtubeInput)) {
      toast.error('This video is already added');
      return;
    }

    setFormData({
      ...formData,
      youtubeVideos: [...formData.youtubeVideos, youtubeInput],
    });
    setYoutubeInput('');
  };

  const removeYoutubeVideo = (index: number) => {
    const updated = formData.youtubeVideos.filter((_, i) => i !== index);
    setFormData({ ...formData, youtubeVideos: updated });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await api.put(`/listings/${listingId}`, formData);
      toast.success('Listing updated successfully!');
      navigate('/my-listings');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to update listing');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Listing</h1>

      {/* Admin Disabled Warning */}
      {formData.adminDisabled && (
        <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800 font-semibold">
                This listing has been disabled by an administrator and cannot be reactivated.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        {/* Default Fields */}
        <ListingNameInput
          value={formData.name}
          onChange={(value) => setFormData({ ...formData, name: value })}
          listingId={listingId}
          required
        />

        <Input
          id="listing-title"
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />

        <Textarea
          id="listing-description"
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          rows={5}
        />

        <Input
          id="listing-address"
          label="Address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          required
        />

        <Select
          id="listing-timezone"
          label="Timezone"
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          options={COMMON_TIMEZONES}
          helperText="Select the timezone for this listing. All booking times will be shown in this timezone."
        />

        {/* Photos */}
        <PhotoUploader
          photos={formData.photos}
          onChange={(photos) => setFormData({ ...formData, photos })}
          listingId={listingId}
        />

        {/* YouTube Videos */}
        <div>
          <label htmlFor="youtube-url-input" className="block text-sm font-medium text-gray-700 mb-2">
            YouTube Videos
          </label>
          <div className="space-y-3">
            {formData.youtubeVideos.map((video, index) => (
              <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                <span className="flex-1 text-sm text-gray-700 truncate">{video}</span>
                <button
                  type="button"
                  onClick={() => removeYoutubeVideo(index)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                id="youtube-url-input"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeInput}
                onChange={(e) => setYoutubeInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addYoutubeVideo();
                  }
                }}
              />
              <Button type="button" onClick={addYoutubeVideo} size="sm">
                Add
              </Button>
            </div>
          </div>
        </div>

        <Input
          id="support-phone-number"
          label="Support Phone Number"
          value={formData.supportPhoneNumber}
          onChange={(e) => setFormData({ ...formData, supportPhoneNumber: e.target.value })}
        />

        <Select
          id="show-support-phone"
          label="Show Support Phone"
          value={formData.showSupportPhone}
          onChange={(e) => setFormData({ ...formData, showSupportPhone: e.target.value })}
          options={[
            { value: 'everyone', label: 'Everyone (with CAPTCHA)' },
            { value: 'renters_only', label: 'Renters Only' },
            { value: 'hide', label: 'Hide' },
          ]}
        />

        {/* Preferred Slot View */}
        <div>
          <label htmlFor="preferred-slot-view" className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Slot View
          </label>
          <Select
            id="preferred-slot-view"
            value={formData.preferredSlotView}
            onChange={(e) => setFormData({ ...formData, preferredSlotView: e.target.value })}
            options={[
              { value: 'list', label: 'List View' },
              { value: 'grid', label: 'Grid View' },
            ]}
          />
          <p className="text-sm text-gray-500 mt-1">
            Grid view shows daily time slots in a calendar-style grid. Only applies to listings with daily rental windows.
          </p>
        </div>

        {/* Show Calendar Date Picker */}
        <div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showCalendar}
              onChange={(e) => setFormData({ ...formData, showCalendar: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Show Calendar Date Picker</span>
          </label>
          <p className="text-sm text-gray-500 mt-1">
            When enabled, users can select dates from a calendar. When disabled, slots are shown in chronological order with pagination.
          </p>
        </div>

        {/* Show Availability Above Description */}
        <div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showAvailabilityAboveDescription}
              onChange={(e) => setFormData({ ...formData, showAvailabilityAboveDescription: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Show availability above description</span>
          </label>
          <p className="text-sm text-gray-500 mt-1">
            Display rental calendar and time slots before the description section
          </p>
        </div>

        {/* Listing Status */}
        <div>
          <label htmlFor="listing-status" className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <Select
            id="listing-status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            disabled={formData.adminDisabled}
          />
          {formData.adminDisabled && (
            <p className="text-sm text-red-600 mt-1">
              Status cannot be changed while listing is admin-disabled
            </p>
          )}
        </div>

        {/* Manage Windows Section */}
        <div className="border-t pt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Rental Windows</h3>
                <p className="text-sm text-gray-600">
                  Configure when and how your listing can be rented by managing rental windows.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => navigate(`/listings/${listingId}/windows`)}
              >
                Manage Windows
              </Button>
            </div>
          </div>
        </div>

        {/* Advanced Fields */}
        <div className="border-t pt-6">
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex justify-between items-center w-full text-left">
                  <h3 className="text-lg font-semibold text-gray-900">Advanced Settings</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      open ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </Disclosure.Button>
                <Disclosure.Panel className="mt-4 space-y-4">
                  <Input
                    id="advance-booking-limit"
                    type="number"
                    label="Advance Booking Limit (days)"
                    value={formData.advanceBookingLimit}
                    onChange={(e) =>
                      setFormData({ ...formData, advanceBookingLimit: parseInt(e.target.value) || 365 })
                    }
                    helperText="How many days in advance users can book"
                  />

                  <label htmlFor="show-on-map" className="flex items-center gap-2 text-sm">
                    <input
                      id="show-on-map"
                      name="show-on-map"
                      type="checkbox"
                      checked={formData.showOnMap}
                      onChange={(e) => setFormData({ ...formData, showOnMap: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-gray-700">Show on map</span>
                  </label>

                  <Select
                    id="show-address"
                    label="Show Address"
                    value={formData.showAddress}
                    onChange={(e) => setFormData({ ...formData, showAddress: e.target.value })}
                    options={[
                      { value: 'public', label: 'Public (No CAPTCHA)' },
                      { value: 'captcha', label: 'Everyone (with CAPTCHA)' },
                      { value: 'renters_only', label: 'Renters Only' },
                      { value: 'hide', label: 'Hide' },
                    ]}
                  />

                  <div>
                    <label htmlFor="cancellation-quantity" className="block text-sm font-medium text-gray-700 mb-2">
                      Cancellation Window
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        id="cancellation-quantity"
                        type="number"
                        value={formData.cancellationWindow.quantity}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cancellationWindow: {
                              ...formData.cancellationWindow,
                              quantity: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                      />
                      <Select
                        id="cancellation-units"
                        value={formData.cancellationWindow.units}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cancellationWindow: {
                              ...formData.cancellationWindow,
                              units: e.target.value as 'weeks' | 'days' | 'hours' | 'minutes',
                            },
                          })
                        }
                        options={[
                          { value: 'minutes', label: 'Minutes' },
                          { value: 'hours', label: 'Hours' },
                          { value: 'days', label: 'Days' },
                          { value: 'weeks', label: 'Weeks' },
                        ]}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="transition-quantity" className="block text-sm font-medium text-gray-700 mb-2">
                      Transition Time
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        id="transition-quantity"
                        type="number"
                        value={formData.transitionTime.quantity}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            transitionTime: {
                              ...formData.transitionTime,
                              quantity: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                      />
                      <Select
                        id="transition-units"
                        value={formData.transitionTime.units}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            transitionTime: {
                              ...formData.transitionTime,
                              units: e.target.value as 'weeks' | 'days' | 'hours' | 'minutes',
                            },
                          })
                        }
                        options={[
                          { value: 'minutes', label: 'Minutes' },
                          { value: 'hours', label: 'Hours' },
                          { value: 'days', label: 'Days' },
                          { value: 'weeks', label: 'Weeks' },
                        ]}
                      />
                    </div>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>

        {/* Renter Detail Requirements */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Renter Detail Requirements</h3>
              <p className="text-sm text-gray-600 mt-1">
                Add custom fields that renters must fill out when booking
              </p>
            </div>
            <Button type="button" size="sm" onClick={addRenterDetailField}>
              Add Field
            </Button>
          </div>

          {formData.renterDetailRequirements.length === 0 ? (
            <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
              No custom fields added yet. Click "Add Field" to create one.
            </p>
          ) : (
            <div className="space-y-4">
              {formData.renterDetailRequirements.map((field, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between items-start">
                    <h4 className="text-sm font-medium text-gray-700">Field {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeRenterDetailField(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  <Input
                    id={`renter-field-name-${index}`}
                    label="Field Name"
                    value={field.fieldName}
                    onChange={(e) => updateRenterDetailField(index, 'fieldName', e.target.value)}
                    placeholder="e.g., Car Make, Phone Number"
                    required
                  />
                  <Textarea
                    id={`renter-field-description-${index}`}
                    label="Description / Help Text"
                    value={field.description}
                    onChange={(e) => updateRenterDetailField(index, 'description', e.target.value)}
                    placeholder="e.g., What brand of car do you drive? (e.g., Ford, BMW)"
                    rows={2}
                    required
                  />
                  <label htmlFor={`renter-field-required-${index}`} className="flex items-center gap-2 text-sm">
                    <input
                      id={`renter-field-required-${index}`}
                      name={`renter-field-required-${index}`}
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateRenterDetailField(index, 'required', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-gray-700">Required field</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <Button type="submit" isLoading={isSaving}>
            Save Changes
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/my-listings')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditListingPage;
